const STATUS_PRIORITY = {
  PERFECT: 1,
  GOOD: 2,
  NOVEL: 3,
  PARTIAL: 4,
  MIXED: 5,
};

const SCHEME_DISPLAY_NAMES = {
  saureus: 'S. aureus MLST',
  abaumannii: 'A. baumannii Oxford MLST',
  abaumannii_2: 'A. baumannii Pasteur MLST',
};

export function getSchemeDisplayName(scheme) {
  return SCHEME_DISPLAY_NAMES[scheme] || scheme;
}

export function getMlstStatusDisplay(status, sequenceType) {
  const normalized = (status || '').toUpperCase();
  if (normalized === 'PERFECT' && sequenceType) {
    return {
      label: `MLST: ST ${sequenceType}`,
      severity: 'success',
    };
  }

  if (normalized === 'GOOD' && sequenceType) {
    return {
      label: `MLST: ST ${sequenceType}*`,
      severity: 'success',
    };
  }

  if ((normalized === 'PARTIAL' || normalized === 'MIXED') && sequenceType) {
    return {
      label: `MLST: ST ${sequenceType}*`,
      severity: 'warning',
    };
  }

  if (normalized === 'NOVEL') {
    return {
      label: 'MLST: Novel',
      severity: 'info',
    };
  }

  return {
    label: 'MLST: Not Determined',
    severity: 'default',
  };
}

function parseLocusFromGene(gene, fallbackScheme) {
  if (!gene || gene.startsWith('ST:')) return null;

  const locusMatch = gene.match(/^([^:]+):(.+)\(([^)]*)\)$/);
  if (!locusMatch) return null;

  const scheme = locusMatch[1] || fallbackScheme;
  const locusName = locusMatch[2];
  const alleleRaw = locusMatch[3] || '';
  const alleles = alleleRaw
    .split(',')
    .map((value) => value.trim())
    .filter(Boolean);
  const isMissing = alleles.length === 0 || alleles.includes('-');
  const isAmbiguous = alleles.length > 1;

  return {
    scheme,
    name: locusName,
    alleles: alleles.length > 0 ? alleles : ['-'],
    isMissing,
    isAmbiguous,
  };
}

function parseSummarySchemeFromGene(gene, fallbackScheme) {
  if (!gene || !gene.startsWith('ST:')) return fallbackScheme || null;
  const parts = gene.split(':');
  return parts.length >= 3 ? parts[1] : (fallbackScheme || null);
}

export function parseMlstResults(rows = []) {
  if (!Array.isArray(rows) || rows.length === 0) return [];

  const grouped = rows.reduce((acc, row) => {
    const attrs = row?.attributes || {};
    const metadata = attrs.metadata || {};
    const status = (metadata.status || '').toUpperCase();
    const summaryScheme = parseSummarySchemeFromGene(attrs.gene, metadata.scheme);
    const locus = parseLocusFromGene(attrs.gene, summaryScheme);
    const scheme = metadata.scheme || summaryScheme || locus?.scheme;
    if (!scheme) return acc;

    if (!acc[scheme]) {
      acc[scheme] = {
        scheme,
        schemeDisplayName: getSchemeDisplayName(scheme),
        organism: metadata.organism || null,
        sequenceType: metadata.sequence_type || null,
        status: status || null,
        score: metadata.score ?? null,
        tool: metadata.tool || null,
        version: metadata.version || null,
        loci: [],
      };
    }

    if (attrs.gene?.startsWith('ST:')) {
      acc[scheme] = {
        ...acc[scheme],
        organism: metadata.organism || acc[scheme].organism,
        sequenceType: metadata.sequence_type || acc[scheme].sequenceType,
        status: status || acc[scheme].status,
        score: metadata.score ?? acc[scheme].score,
        tool: metadata.tool || acc[scheme].tool,
        version: metadata.version || acc[scheme].version,
      };
    } else if (locus) {
      acc[scheme].loci.push(locus);
    }

    return acc;
  }, {});

  return Object.values(grouped)
    .map((item) => ({
      ...item,
      loci: item.loci.sort((a, b) => a.name.localeCompare(b.name)),
    }))
    .sort((a, b) => {
      const aPriority = STATUS_PRIORITY[a.status] || 99;
      const bPriority = STATUS_PRIORITY[b.status] || 99;
      if (aPriority !== bPriority) return aPriority - bPriority;
      return a.schemeDisplayName.localeCompare(b.schemeDisplayName);
    });
}

export function getMlstInterpretation(status, sequenceType) {
  switch ((status || '').toUpperCase()) {
    case 'PERFECT':
      return 'Exact allele match for all loci in the scheme.';
    case 'GOOD':
      return '* Imperfect match - the closest sequence type is reported but one or more alleles had partial coverage or low identity.';
    case 'MIXED':
      if (sequenceType) {
        return '* Imperfect match - multiple alleles were detected at one or more loci; the closest sequence type is reported.';
      }
      return 'MLST could not be confidently assigned, usually due to low coverage at one or more loci or mixed alleles consistent with a mixed culture.';
    case 'PARTIAL':
      if (sequenceType) {
        return '* Imperfect match - one or more loci were not fully recovered; the closest sequence type is reported.';
      }
      return 'MLST could not be confidently assigned, usually due to low coverage at one or more loci or mixed alleles consistent with a mixed culture.';
    case 'NOVEL':
      return 'All loci were recovered, but this profile is not currently listed in PubMLST and may represent a new sequence type.';
    default:
      return 'MLST could not be confidently determined for this organism.';
  }
}
